import {
  Radar,
  Volume2,
  Bookmark,
  RotateCw,
  CalendarHeart,
  Brain,
} from "lucide-react";
import DriftRadarAnimation from "./DriftRadarAnimation";
import CleanModeAnimation from "./CleanModeAnimation";
import ParkThoughtsAnimation from "./ParkThoughtsAnimation";
import RecoverWhereYouLeftOffAnimation from "./RecoverWhereYouLeftOffAnimation";
import DeadlinesWithoutShameAnimation from "./DeadlinesWithoutShameAnimation";
import SecondBrainAnimation from "./SecondBrainAnimation";

const blocks = [
  {
    icon: Radar,
    title: "Catch drift before it becomes a lost hour",
    body: "Mindrail monitors live focus signals every few seconds and classifies your state — focused, pre-distraction, paralysis, frustration, passive consumption, wandering, creative flow, or hyperfocus risk.",
    benefit: "It notices the slide before you fully disappear into it.",
    accent: "#2563EB",
  },
  {
    icon: Volume2,
    title: "Quiet the internet with Clean Mode",
    body: "One click hides common distraction traps — sidebars, recommendation rails, ads, visual clutter — while keeping the useful page intact.",
    benefit: "Less visual noise. Fewer rabbit holes. More room to think.",
    accent: "#22C55E",
  },
  {
    icon: Bookmark,
    title: "Park thoughts without chasing them",
    body: "Press Ctrl + Shift + P to capture a thought from any page. Mindrail saves it with the source URL and title so you can come back later — without abandoning the task you're on.",
    benefit: "The thought is safe. You do not have to follow it right now.",
    accent: "#F9735B",
  },
  {
    icon: RotateCw,
    title: "Recover where you left off",
    body: "When you step away, Mindrail can snapshot your session and help you resume the tab, task, and context you were in.",
    benefit: 'No more opening your laptop and thinking, "What was I doing?"',
    accent: "#2563EB",
  },
  {
    icon: CalendarHeart,
    title: "Deadlines without shame",
    body: "RSD-aware task handling. Instead of red overdue warnings, tasks drift into soft amber — with options to reschedule, shrink, or intentionally drop.",
    benefit: 'No red panic. No "you failed." Just: "Still relevant?"',
    accent: "#F59E0B",
  },
  {
    icon: Brain,
    title: "A second brain inside your browser",
    body: "Memory holds session recovery, parked thoughts, brain dump, and a language mirror that helps you understand your own thinking patterns — without sending text to a server.",
    benefit: "Unload the mental tabs without losing them.",
    accent: "#7C3AED",
  },
];

export default function Benefits() {
  return (
    <section
      data-testid="benefits-section"
      className="px-6 lg:px-10 py-24 lg:py-32"
    >
      <div className="max-w-6xl mx-auto">
        <div className="fade-up max-w-3xl">
          <p className="text-sm tracking-[0.18em] uppercase font-semibold text-[#475569]">
            Built for ADHD work patterns
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.05]">
            Six small shifts that make the day workable.
          </h2>
        </div>

        <div className="mt-16 space-y-16 lg:space-y-24">
          {blocks.map((b, i) => {
            const flip = i % 2 === 1;
            return (
              <div
                key={i}
                data-testid={`benefit-block-${i}`}
                className={`fade-up grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                  flip ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-3">
                    <span
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: b.accent + "22" }}
                    >
                      <b.icon
                        className="w-4 h-4"
                        strokeWidth={1.5}
                        style={{ color: b.accent }}
                      />
                    </span>
                    <span className="font-mono text-xs text-[#475569]">
                      0{i + 1} / 06
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-2xl sm:text-3xl text-[#0F172A] leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-4 text-[#475569] leading-relaxed text-base">
                    {b.body}
                  </p>
                  <p
                    className="mt-5 font-display text-lg italic"
                    style={{ color: b.accent }}
                  >
                    {b.benefit}
                  </p>
                </div>

                <div className="lg:col-span-5">
                  <div
                    className={`relative rounded-3xl border border-[#E2E8F0] overflow-hidden ${
                      i <= 5
                        ? "aspect-[4/5]"
                        : "aspect-[4/3]"
                    }`}
                    style={{ background: b.accent + "12" }}
                  >
                    <BenefitArt accent={b.accent} variant={i} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BenefitArt({ accent, variant }) {
  // Pure CSS abstract art per benefit, calm and non-illustrative
  if (variant === 0) return <DriftRadarAnimation />;
  if (variant === 1) return <CleanModeAnimation />;
  if (variant === 2) return <ParkThoughtsAnimation />;
  if (variant === 1)
    return (
      <div className="absolute inset-0 p-8 flex flex-col gap-2 justify-center">
        {[80, 60, 40, 25].map((w, i) => (
          <span
            key={i}
            className="h-2.5 rounded-full"
            style={{
              width: `${w}%`,
              background: accent + (i === 3 ? "" : "33"),
            }}
          />
        ))}
      </div>
    );
  if (variant === 2)
    return (
      <div className="absolute inset-0 grid grid-cols-3 gap-3 p-7">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl bg-white/70 border border-[#E2E8F0] flex items-end p-2"
            style={{ borderColor: accent + "44" }}
          >
            <span
              className="block h-1 rounded-full"
              style={{
                width: `${30 + ((i * 13) % 60)}%`,
                background: accent,
              }}
            />
          </div>
        ))}
      </div>
    );
  if (variant === 3) return <RecoverWhereYouLeftOffAnimation />;
  if (variant === 4) return <DeadlinesWithoutShameAnimation />;
  if (variant === 5) return <SecondBrainAnimation />;
  return null;
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: 28 }).map((_, i) => {
            const isAmber = i === 18 || i === 19;
            return (
              <span
                key={i}
                className="w-3 h-3 rounded-sm"
                style={{
                  background: isAmber ? accent : "#E2E8F099",
                }}
              />
            );
          })}
        </div>
      </div>
    );
  return (
    <div className="absolute inset-0 p-7 flex flex-col gap-2 justify-center">
      {["thread A", "thought · saved", "session · 14 min", "win · sent"].map(
        (l, i) => (
          <span
            key={i}
            className="text-[11px] font-mono px-3 py-1.5 rounded-full bg-white/70 border w-fit"
            style={{ borderColor: accent + "55", color: "#0F172A" }}
          >
            {l}
          </span>
        )
      )}
    </div>
  );
}
